import Phaser from 'phaser';
import * as C from 'tower/constants';
import {
  Direction,
  StandardController,
  State,
  StateMachine,
  asyncAnimation,
  asyncPlaySound,
  asyncTween,
  fillArray,
  flash,
  frameMs,
  gridMove,
  makeStandardController,
  oppositeDirection,
  relativeDirection,
  steppedEase,
  wait,
} from 'tower/util';
import Player from 'tower/scenes/battle/player';
import Maze from 'tower/scenes/battle/maze';
import Enemy from 'tower/scenes/battle/enemy';
import Gel from 'tower/scenes/battle/enemy/gel';
import gameState from 'tower/state/game-state';
import Item from 'tower/scenes/battle/item';
import Room, { RoomBody } from 'tower/scenes/battle/room';
import FLOOR1 from 'tower/scenes/battle/floors';
import { RoomConfig } from 'tower/types';
import Knight from './enemy/knight';
import Flame from './flame';
import Projectile from './enemy/projectile';
import Ghost from './enemy/ghost';
import Wizard from './enemy/wizard';
import Kobold from './enemy/kobold';
import Wisp from './enemy/wisp';
import Boss from './boss';
import Centaur from './enemy/centaur';
import Mummy from './enemy/mummy';
import BaseScene from '../base';
import { CreditsOptions } from '../credits';

export default class BattleScene extends BaseScene {
  controller!: StandardController;
  player!: Player;

  music!: Phaser.Sound.NoAudioSound | Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound;
  hurtMusic!: Phaser.Sound.NoAudioSound | Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound;
  currentRoom!: Room;
  floor!: Floor;
  stateMachine!: StateMachine<PlayingState>;

  public deathState = 'death';

  constructor(config: Phaser.Types.Scenes.SettingsConfig = {}) {
    super({
      key: 'battle',
      ...config,
    });
  }

  loadResources(scene: Phaser.Scene) {
    Player.preload(scene);
    Maze.preload(scene);
    Enemy.preload(scene);
    Knight.preload(scene);
    Item.preload(scene);
    Room.preload(scene);
    Flame.preload(scene);
    Projectile.preload(scene);
    Boss.preload(scene);

    scene.load.spritesheet('tiles', 'img/tiles.png', { frameWidth: 8, frameHeight: 8 });
    scene.load.image('lava', 'img/lava.png');
    scene.load.image('doorMask', 'img/doormask.png');
    scene.load.audio('playerDeath', 'audio/player_death.mp3');
    scene.load.audio('blip', 'audio/blip.mp3');
    scene.load.audio('shortBlip', 'audio/shortblip.mp3');
    scene.load.audio('dungeon', 'audio/dungeon.mp3');
    scene.load.audio('dungeonHurt', 'audio/dungeon_hurt.mp3');
    scene.load.audio('secret', 'audio/secret.mp3');
    scene.load.audio('fairy', 'audio/fairy.mp3');
    scene.load.audio('squeak', 'audio/squeak.mp3');
    scene.load.audio('steps', 'audio/steps.mp3');
  }

  create() {
    Player.create(this);
    Enemy.create(this);
    Gel.create(this);
    Knight.create(this);
    Item.create(this);
    Flame.create(this);
    Projectile.create(this);
    Wizard.create(this);
    Kobold.create(this);
    Wisp.create(this);
    Boss.create(this);
    Centaur.create(this);
    Mummy.create(this);

    this.sound.pauseOnBlur = false;

    // Run the HUD scene in parallel with this one
    this.scene.launch('hud');
    this.events.once('shutdown', () => {
      this.currentRoom.destroy();
      this.scene.stop('hud');
    });

    this.controller = makeStandardController(this);

    // Player
    this.player = new Player(this, 0, 0);

    // Room and floor
    this.floor = new Floor(this, FLOOR1);
    this.currentRoom = this.floor.getRoom(gameState.checkpointRoomKey)!;
    gameState.currentRoomKey = gameState.checkpointRoomKey;
    this.currentRoom.create();

    // Position player spawn spot or room corner
    const playerSpawn = this.currentRoom.roomConfig.playerSpawn;
    if (playerSpawn !== undefined) {
      this.player.setPosition(...this.currentRoom.maze.spaceXYToWorldXY(playerSpawn.spaceX, playerSpawn.spaceY));
    } else {
      this.player.setPosition(...this.currentRoom.maze.spaceXYToWorldXY(0, 0));
    }

    // Music
    this.music = this.sound.add('dungeon', { loop: true });
    this.hurtMusic = this.sound.add('dungeonHurt', { loop: true });
    if (gameState.hp <= C.HURT_MUSIC_THRESHOLD) {
      this.hurtMusic.play();
    } else {
      this.music.play();
    }

    // Change which music is playing based on health
    const handleHealthChange = (hp: number) => {
      if (hp > C.HURT_MUSIC_THRESHOLD && this.hurtMusic.isPlaying) {
        this.hurtMusic.pause();
        this.music.play({ seek: this.hurtMusic.seek });
      } else if (hp <= C.HURT_MUSIC_THRESHOLD && this.music.isPlaying) {
        this.music.pause();
        this.hurtMusic.play({ seek: this.music.seek });
      }
    };
    gameState.on('healthchange', handleHealthChange);
    this.events.once('shutdown', () => {
      gameState.off('healthchange', handleHealthChange);
    });

    // Follow player with camera
    this.cameras.main.setViewport(0, 0, C.CAMERA_VIEWPORT_WIDTH, C.CAMERA_VIEWPORT_HEIGHT);
    this.currentRoom.lockCamera();

    this.stateMachine = new StateMachine(gameState.checkpointRoomKey === 'start' ? 'entering' : 'playing', {
      entering: new EnteringState(this),
      playing: new PlayingState(this),
      roomTransition: new RoomTransitionState(this),
      frozen: new FrozenState(this),
      death: new DeathState(this),
      ghostDeath: new GhostDeathState(this),
      endGame: new EndGameState(this),
    });

    // MapMaking mode: Click to create/destroy walls, use spacebar to output maze to JS console
    if (window.location.search.includes('mapmaking')) {
      this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        const [node, direction] = this.currentRoom.maze.getLinkAtWorldXY(pointer.worldX, pointer.worldY);
        if (node !== null) {
          this.currentRoom.maze.toggleWall(node, direction);
        } else {
          const [x, y] = this.currentRoom.maze.worldXYToSpaceXY(pointer.worldX, pointer.worldY);
          console.log(`Space: ${x},${y}`);
        }
      });

      this.input.keyboard?.addKey('SPACE').on('down', () => {
        console.log(this.currentRoom.maze.serializeLinks());
      });
    }

    this.events.emit('roomtransitioncomplete');

    gameState.setFlag('startedGame', true);
  }

  get isPlaying() {
    return this.stateMachine.state === 'playing';
  }

  stopMusic() {
    this.music.stop();
    this.hurtMusic.stop();
  }

  playMusic() {
    if (gameState.hp <= C.HURT_MUSIC_THRESHOLD) {
      this.hurtMusic.play();
    } else {
      this.music.play();
    }
  }

  update() {
    this.stateMachine.update();
  }

  freeze() {
    this.stateMachine.transition('frozen');
  }

  unfreeze() {
    this.stateMachine.transition('playing');
  }

  handlePlayerDeath() {
    this.stateMachine.transition(this.deathState);
  }

  endGame() {
    this.stateMachine.transition('endGame');
  }

  async healPlayer(amount: number) {
    const shortBlip = this.sound.add('shortBlip');
    shortBlip.play({ loop: true });
    while (gameState.hp < gameState.maxHp && amount > 0) {
      gameState.hp += 1;
      await wait(this, 250);
    }
    shortBlip.destroy();
    this.sound.play('blip');
  }
}

class BattleSceneState extends State {
  constructor(protected scene: BattleScene) {
    super();
  }
}

class EnteringState extends BattleSceneState {
  async handleEntered() {
    const { scene } = this;
    const { player } = scene;

    // Position player under bottom door and walk in
    const door = scene.currentRoom.doors.down!;
    door.state = 'open';
    player.body.reset(door.x, door.y);
    player.setVisible(true);

    scene.events.emit('roomtransitionplayerenter');

    const playerMoveDistance = 24;
    const [px, py] = gridMove(player.x, player.y, Direction.Up, playerMoveDistance);
    player.direction = Direction.Up;
    player.play('playerWalk[up]');
    await asyncTween(scene, {
      targets: [player],
      x: px,
      y: py,
      duration: frameMs(16),
      ease: steppedEase(playerMoveDistance),
    });
    player.stateMachine.transition('idle');

    door.state = 'closed';
    scene.sound.play('openDoor');

    return this.transition('playing');
  }
}

class PlayingState extends BattleSceneState {
  update() {
    const { scene } = this;
    scene.player.update();
    scene.currentRoom.update();

    // Check for room transition
    let newRoom: Room | null = null;
    scene.physics.overlap(
      scene.floor.getRooms().map((r) => r!.body),
      scene.player,
      (body) => {
        const { room } = body as RoomBody;
        if (room !== scene.currentRoom) {
          newRoom = room;
        }
      },
    );
    if (newRoom != null) {
      this.transition('roomTransition', newRoom);
    }
  }
}

/** Freeze processing things for some async process (e.g. getting an item) */
class FrozenState extends BattleSceneState {
  handleEntered() {
    this.scene.physics.pause();
    this.scene.anims.pauseAll();
    this.scene.events.emit('freeze');
  }

  handleExited() {
    this.scene.events.emit('unfreeze');
    this.scene.physics.resume();
    this.scene.anims.resumeAll();
  }
}

class RoomTransitionState extends BattleSceneState {
  async handleEntered(newRoom: Room) {
    const { scene } = this;
    const { currentRoom, player } = scene;

    scene.events.emit('roomtransitionstart', newRoom, currentRoom);

    // Prep for transition
    newRoom.create();
    currentRoom.destroyEntities();
    player.setVisible(false);
    player.body.stop();

    // If moving into a dark room, fade to dark first
    if (newRoom.isDark && !currentRoom.isDark) {
      await currentRoom.transitionDark(true);
    }

    // If moving out of a dark room, ensure the new room is dark until we move away
    if (!newRoom.isDark && currentRoom.isDark) {
      newRoom.setVisuallyDark(true);
    }

    // Slide camera towards new room
    const direction = relativeDirection(currentRoom, newRoom);
    const distance = [Direction.Up, Direction.Down].includes(direction)
      ? C.CAMERA_VIEWPORT_HEIGHT
      : C.CAMERA_VIEWPORT_WIDTH;
    let [nx, ny] = gridMove(scene.cameras.main.scrollX, scene.cameras.main.scrollY, direction, distance);

    // Special case: If we're moving vertically and the player is off-center, we want to subtly shift the camera
    // x so that it is centered on the door. Otherwise, the camera will jump once we re-lock it.
    const newDoor = newRoom.doors[oppositeDirection(direction)]!;
    if ([Direction.Up, Direction.Down].includes(direction)) {
      nx = newDoor.x - C.CAMERA_VIEWPORT_WIDTH / 2;
    }

    currentRoom.unlockCamera();
    await asyncTween(scene, {
      targets: [scene.cameras.main],
      scrollX: nx,
      scrollY: ny,
      ny,
      duration: frameMs(100),
      ease: steppedEase(distance / 8),
    });

    // Clean up old room
    currentRoom.destroy();

    // Light up the new room if needed
    if (!newRoom.isDark && newRoom.visuallyDark) {
      await newRoom.transitionDark(false);
    }

    // Position player under new door and walk in
    player.body.reset(newDoor.x, newDoor.y);
    player.setVisible(true);

    scene.events.emit('roomtransitionplayerenter');

    const playerMoveDistance = 16;
    const [px, py] = gridMove(player.x, player.y, direction, playerMoveDistance);
    player.play(`playerWalk[${direction}]`);
    await asyncTween(scene, {
      targets: [player],
      x: px,
      y: py,
      duration: frameMs(16),
      ease: steppedEase(playerMoveDistance),
    });
    player.stateMachine.transition('idle');

    // Swap room
    scene.currentRoom = newRoom;
    gameState.currentRoomKey = newRoom.roomConfig.key;
    newRoom.lockCamera();

    // Room cooldowns
    gameState.mattockDurability = gameState.maxMattockDurability;
    gameState.candleDurability = gameState.maxCandleDurability;
    for (const key in scene.player.itemThrown) {
      scene.player.itemThrown[key as keyof typeof scene.player.itemThrown] = false;
    }

    newRoom.roomConfig.onPostRoomTransition?.(scene, newRoom);
    scene.events.emit('roomtransitioncomplete', newRoom);

    // Get back to playin
    return this.transition('playing');
  }
}

class DeathState extends BattleSceneState {
  async handleEntered() {
    const { scene } = this;
    const { player, currentRoom } = scene;

    currentRoom.destroyEntities();

    // Flash
    player.play('playerWalk[down]');
    await new Promise((resolve) => player.flash(4).on('complete', resolve));

    // Pause + halt music
    await wait(scene, frameMs(16));
    scene.sound.stopAll();

    // Spin + death music
    scene.sound.play('playerDeath');
    await Promise.all([
      asyncAnimation(player, 'playerSpin'),
      wait(scene, 1000)
        .then(() => scene.currentRoom.transitionDark(true))
        .then(() => scene.currentRoom.setVisible(false)),
    ]);

    // Statue mode
    scene.player.setFrame(9);
    await wait(scene, frameMs(40));

    // Blink out
    scene.sound.play('blip');
    await asyncAnimation(player, 'playerDie');
    player.setVisible(false);

    // Reset back to level intro
    await wait(scene, 500);
    gameState.deathReset();
    scene.scene.start('level-intro');
  }
}

class GhostDeathState extends BattleSceneState {
  async handleEntered() {
    const { scene } = this;
    const { player, currentRoom } = scene;

    // Get rid of projectiles
    for (const projectile of currentRoom.projectiles.children) {
      projectile.destroy(true);
    }

    // Halt all the ghost movement
    for (const enemy of currentRoom.enemies.children) {
      const enemyGhost = enemy as Ghost;
      enemyGhost.body.setVelocity(0);
    }

    // Flash
    player.play('playerWalk[down]');
    await new Promise((resolve) => player.flash(4).on('complete', resolve));

    // Pause + halt music
    await wait(scene, frameMs(16));
    scene.music.stop();
    scene.hurtMusic.stop();

    // Spin + death music + fade out
    scene.sound.play('playerDeath');
    await Promise.all([
      asyncAnimation(player, 'playerSpin'),
      wait(scene, 1000)
        .then(() => scene.currentRoom.transitionDark(true))
        .then(() => currentRoom.setVisible(false)),
    ]);

    // Go grey and wait a bit
    player.setFrame(9);
    await wait(scene, 5000);

    // If player is too high up ghost needs to appear below
    let ghost: Phaser.GameObjects.Sprite;
    let book: Item;
    if (currentRoom.maze.worldXYToSpaceXY(player.x, player.y)[1] > 1) {
      // GHOST ABOVE
      // Ghost flickers and appears
      ghost = scene.add.sprite(player.x, player.y - 48, 'enemies', 128);
      player.setFrame(25);
      scene.sound.play('magic');
      await flash(scene, [ghost], 6, 500);
      await wait(scene, 500);

      // Book comes out of ghost towards player
      book = new Item(scene, ghost.x, ghost.y + 16, 'bookOfTruth');
      await flash(scene, [book], 6, 500);
      await wait(scene, 500);

      await asyncTween(scene, {
        targets: [book],
        y: '+= 24',
        duration: 1000,
      });
    } else {
      // GHOST BELOW
      // Ghost flickers and appears
      ghost = scene.add.sprite(player.x, player.y + 48, 'enemies', 128);
      scene.sound.play('magic');
      await flash(scene, [ghost], 6, 500);
      await wait(scene, 500);

      // Book comes out of ghost towards player
      ghost.setFrame(129);
      book = new Item(scene, ghost.x, ghost.y - 16, 'bookOfTruth').setDepth(C.DEPTHS.Player + 1);
      await flash(scene, [book], 6, 500);
      await wait(scene, 500);

      await asyncTween(scene, {
        targets: [book],
        y: '-= 24',
        duration: 1000,
      });
    }

    // Player plays got item fanfare and gets item while ghosts all appear
    gameState.addItem(book.type);
    player.holdItem(book);

    const enemyFlashes: Promise<void>[] = [];
    for (const enemy of currentRoom.enemies.children) {
      const enemyGhost = enemy as Ghost;
      enemyGhost.setVisible(true);
      enemyGhost.invulnerable = false;
      enemyFlashes.push(flash(scene, [enemyGhost], 6, 500));
    }
    await Promise.all([
      asyncPlaySound(Item.bigItemSound),
      flash(scene, [ghost], 6, 500).then(() => ghost.destroy()),
      scene.healPlayer(gameState.maxHp),
      ...enemyFlashes,
    ]);

    // Fade back in
    currentRoom.setVisible(true);
    await currentRoom.transitionDark(false);

    // Reset room state
    scene.playMusic();
    player.idle();
    book.destroy();
    for (const enemy of currentRoom.enemies.children) {
      const enemyGhost = enemy as Ghost;
      enemyGhost.stateMachine.transition('initial');
    }

    gameState.solveRoom('therearemanydoors');
    scene.deathState = 'death';

    return this.transition('playing');
  }
}

class EndGameState extends BattleSceneState {
  async handleEntered() {
    const { scene } = this;
    const { player, currentRoom } = scene;

    const door = currentRoom.doors.up!;

    scene.stopMusic();
    player.body.reset(door.x, door.y + 12);

    scene.add.image(door.x, door.y, 'doorMask').setDepth(C.DEPTHS.Ceiling);
    scene.currentRoom.unlockCamera();

    const steps = scene.sound.add('steps');
    steps.play({ loop: true });

    player.play({ key: 'playerWalk[up]', frameRate: 3 });
    await asyncTween(scene, {
      targets: [player],
      y: '-=10',
      duration: 500,
      ease: steppedEase(5),
    });

    player.play({ key: 'playerWalk[right]', frameRate: 3 });
    await asyncTween(scene, {
      targets: [player],
      y: '-=8',
      x: '+=16',
      duration: 1000,
      ease: steppedEase(8),
    });

    steps.stop();
    await wait(scene, 1500);

    const options: CreditsOptions = {
      context: 'ending',
    };
    scene.scene.start('credits', options);
  }
}

export class Floor {
  rooms: (Room | null)[] = fillArray(C.FLOOR_WIDTH_ROOMS * C.FLOOR_HEIGHT_ROOMS, () => null);

  constructor(scene: BattleScene, roomConfigs: RoomConfig[]) {
    for (const roomConfig of roomConfigs) {
      const { floorX, floorY } = roomConfig;
      this.rooms[floorY * C.FLOOR_WIDTH_ROOMS + floorX] = new Room(scene, this, roomConfig);
    }
  }

  getRooms() {
    return this.rooms.filter((r) => r !== null);
  }

  getRoom(key: string) {
    return this.rooms.find((r) => r?.roomConfig.key === key);
  }

  getRoomAt(floorX: number, floorY: number) {
    return this.rooms[floorY * C.FLOOR_WIDTH_ROOMS + floorX];
  }

  getAdjacentRoom(floorX: number, floorY: number, direction: Direction) {
    return this.getRoomAt(...gridMove(floorX, floorY, direction));
  }
}
